import { isReference } from '@apollo/client';
import type { FetchResult, StoreObject } from '@apollo/client';

import { isDevelopment } from '@constants/env';

import { useResponseHandler } from '@hooks/useResponseHandler';
import { useSession } from '@hooks/useSession';

import { EncounterRecord } from '@models/encounter';
import type { GetFhirRecordParams } from '@models/fhir-resource';

import { parseArgs, type CachedRecordList } from '@utils/apollo';

import {
  useGetHospitalizationQuery,
  useGetHospitalizationsRecordQuery,
  usePostHospitalizationsRecordMutation,
  useUpdateHospitalizationsEncounterRecordMutation,
  useDeleteHospitalizationRecordMutation,
} from 'generated/hospitalization.generated';
import type {
  PostHospitalizationsRecordMutationVariables,
  UpdateHospitalizationsEncounterRecordMutationVariables,
  DeleteHospitalizationRecordMutation,
  DeleteHospitalizationRecordMutationVariables,
} from 'generated/hospitalization.generated';
import type { GetPatientHealthSummaryRecordsQuery } from 'generated/patient-record.generated';
import { GetPatientHealthSummaryRecordsDocument } from 'generated/patient-record.generated';

export const useGetHospitalizations = () => {
  const { handleError } = useResponseHandler();
  const {
    state: { activeProfile },
  } = useSession();
  const patient_id = activeProfile?.patient_id ?? '';
  const response = useGetHospitalizationsRecordQuery({
    variables: { input: { patient: patient_id, count: 1500, page: 1 } },
    skip: !patient_id,
  });
  const { data, error } = response;
  handleError(error);
  const records = (data?.listHospitalizationEncounters.data ?? []).map(
    (r) => new EncounterRecord(r)
  );
  return { ...response, data: records };
};

export const useGetHospitalization = (params: GetFhirRecordParams) => {
  const { handleError } = useResponseHandler();
  const {
    state: { activeProfile },
  } = useSession();
  const patient_id = activeProfile?.patient_id ?? '';
  const response = useGetHospitalizationQuery({
    variables: { input: { ...params, patient_id } },
    skip:
      !patient_id ||
      !params.record_id ||
      params.record_id === 'add' ||
      Boolean(params.skip),
  });
  const { data, error } = response;
  handleError(error);
  const record = data?.getHospitalizationEncounter;
  return { ...response, data: record ? new EncounterRecord(record) : null };
};

export function useCreateHospitalization(): [
  (
    params: PostHospitalizationsRecordMutationVariables
  ) => Promise<FetchResult<EncounterRecord>>,
  boolean,
] {
  const [mutate, { loading: baseLoading }] =
    usePostHospitalizationsRecordMutation();
  const loading = baseLoading;

  async function createHospitalization(
    params: PostHospitalizationsRecordMutationVariables
  ) {
    const response = await mutate({
      variables: { ...params },
      update: (cache, { data: response }) => {
        if (!response?.postHospitalizationsEncounterRecord) {
          return;
        }
        const storeObject =
          response.postHospitalizationsEncounterRecord as unknown as StoreObject;
        try {
          const { encounter } = params.input;
          cache.modify<{ listHospitalizationEncounters: CachedRecordList }>({
            fields: {
              listHospitalizationEncounters: (
                existing,
                { storeFieldName, toReference }
              ) => {
                if (isReference(existing)) {
                  return existing;
                }
                const parsed = parseArgs(storeFieldName);
                if (parsed?.patient === encounter.subject?.reference) {
                  return {
                    ...existing,
                    data: [...existing.data, toReference(storeObject)],
                  };
                }
                return existing;
              },
            },
          });

          cache.updateQuery<GetPatientHealthSummaryRecordsQuery>(
            {
              query: GetPatientHealthSummaryRecordsDocument,
              variables: {
                input: {
                  patient: encounter.subject?.reference,
                },
                hospitalizations_encounter: true,
              },
            },
            (data) => ({
              getPatientHealthSummaryRecords: {
                hospitalizations_encounter: [
                  ...(data?.getPatientHealthSummaryRecords
                    .hospitalizations_encounter ?? []),
                  response.postHospitalizationsEncounterRecord,
                ],
              },
            })
          );
        } catch (e) {
          if (isDevelopment) {
            // eslint-disable-next-line no-console
            console.error(e);
          }
        }
      },
    });
    const createdHosp = response.data?.postHospitalizationsEncounterRecord;
    return {
      ...response,
      data: createdHosp ? new EncounterRecord(createdHosp) : undefined,
    };
  }
  return [createHospitalization, loading];
}

export function useUpdateHospitalization(): [
  (
    params: UpdateHospitalizationsEncounterRecordMutationVariables
  ) => Promise<FetchResult<EncounterRecord>>,
  boolean,
] {
  const [mutate, { loading: baseLoading }] =
    useUpdateHospitalizationsEncounterRecordMutation();
  const loading = baseLoading;

  async function updateHospitalization(
    params: UpdateHospitalizationsEncounterRecordMutationVariables
  ) {
    const response = await mutate({
      variables: { ...params },
    });
    const updatedHosp = response.data?.updateHospitalizationsEncounterRecord;
    return {
      ...response,
      data: updatedHosp ? new EncounterRecord(updatedHosp) : undefined,
    };
  }
  return [updateHospitalization, loading];
}

export function useDeleteHospitalization(): [
  (
    params: DeleteHospitalizationRecordMutationVariables
  ) => Promise<FetchResult<DeleteHospitalizationRecordMutation>>,
  boolean,
] {
  const [mutate, { loading: baseLoading }] =
    useDeleteHospitalizationRecordMutation();
  const loading = baseLoading;

  async function deleteHospitalization(
    params: DeleteHospitalizationRecordMutationVariables
  ) {
    const response = await mutate({
      variables: { ...params },
      update: (cache, { data: response }) => {
        if (!response?.deleteHospitalizationRecord) {
          return;
        }
        const { encounter_id: id } = params.input;
        try {
          const normalizedId = cache.identify({
            id,
            __typename: 'EncounterModel',
          });
          cache.evict({ id: normalizedId });
          cache.gc();
        } catch (e) {
          if (isDevelopment) {
            // eslint-disable-next-line no-console
            console.error(e);
          }
        }
      },
    });
    return response;
  }
  return [deleteHospitalization, loading];
}
